/**
 * CompactStatusDot - Minimal status indicator
 *
 * A small colored dot that indicates connection status.
 * Optionally shows a tooltip with status text on hover.
 */

import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import type { CompactStatus } from '@/types/connectionStatus';
import { getCompactStatusLabel, getCompactStatusColorKind } from '@/utils/connections';

const useStyles = makeStyles({
  dot: {
    borderRadius: '50%',
    flexShrink: 0,
  },
  small: {
    width: '6px',
    height: '6px',
  },
  medium: {
    width: '8px',
    height: '8px',
  },
  connected: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
  },
  warning: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
  error: {
    backgroundColor: tokens.colorPaletteRedForeground1,
  },
  none: {
    backgroundColor: tokens.colorNeutralForeground4,
  },
});

export interface CompactStatusDotProps {
  /** Connection status */
  status: CompactStatus;
  /** Dot size - small (6px) or medium (8px) */
  size?: 'small' | 'medium';
  /** Whether to show tooltip on hover */
  showTooltip?: boolean;
  /** Custom tooltip content (overrides default) */
  tooltipContent?: string;
  /** Additional CSS class */
  className?: string;
}

/**
 * CompactStatusDot - Minimal status indicator component
 */
export const CompactStatusDot = ({
  status,
  size = 'medium',
  showTooltip = true,
  tooltipContent,
  className,
}: CompactStatusDotProps) => {
  const styles = useStyles();

  const statusClass = useMemo(
    () => styles[getCompactStatusColorKind(status)],
    [status, styles],
  );

  const sizeClass = size === 'small' ? styles.small : styles.medium;
  const dotClassName = mergeClasses(styles.dot, sizeClass, statusClass, className);

  const dot = (
    <span
      className={dotClassName}
      role="img"
      aria-label={getCompactStatusLabel(status)}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip
        content={tooltipContent ?? getCompactStatusLabel(status)}
        relationship="label"
      >
        {dot}
      </Tooltip>
    );
  }

  return dot;
};

CompactStatusDot.displayName = 'CompactStatusDot';
